import { VoyageData, VoyageModel } from 'models/VoyageModel';
import { ApiBase } from 'utils/api';
import { forceDownload, objectToFormData } from 'utils/common';


export default class VoyageApi {
    private base: ApiBase;

    constructor(base: ApiBase) {
        this.base = base;
    }

    count = () => this.base.axios
        .get('/api/voyages/count')
        .then(res => res.data);

    all = (status: string) => this.base.axios
        .get(`/api/voyages`, {params: {status}})
        .then(res => res.data);

    get = (id: number) => this.base.axios
        .get(`/api/voyages/${id}`)
        .then(res => res.data);

    create = (data: VoyageData) => this.base.axios
        .post(`/api/voyages`, data);

    update = (data: VoyageModel) => this.base.axios
        .put(`/api/voyages/${data.id}`, data);

    cancel = (id: number, reason: string) => this.base.axios
        .post(`/api/voyages/${id}/canceled`, {reason});

    loaded = (id: number, force: boolean = false) => this.base.axios
        .post(`/api/voyages/${id}/loaded`, {force});

    departed = (id: number) => this.base.axios
        .post(`/api/voyages/${id}/departed`);

    arrived = (id: number) => this.base.axios
        .post(`/api/voyages/${id}/arrived`);

    lost = (id: number, reason: string) => this.base.axios
        .post(`/api/voyages/${id}/lost`, {reason});

    manifest = (id: number, type?: string) => this.base.axios
        .get(`/api/voyages/${id}/manifest`, {
            responseType: 'blob',
            params: {type},
        });

    downloadBL = (id: number, type: string, company_id?: number) => this.base.axios
        .get(`/api/voyages/${id}/bl/f`, {
            responseType: 'blob',
            params: {type, company_id},
        });

    ec = (id: number) => this.base.axios
        .get(`/api/voyages/${id}/ec`, {
            responseType: 'blob',
        });

    invoices = (id: number, company_id?: number, format?: string) => this.base.axios
        .get(`/api/voyages/${id}/invoices`, {
            responseType: 'blob',
            params: {company_id, format},
        })
        .then(forceDownload);

    makeToyamaInvoice = (id: number, data: Record<string, any>) => this.base.axios
        .post(`/api/voyages/${id}/toyama-invoices`, data, {responseType: 'blob'})
        .then(forceDownload)

    getToyamaInvoice = (id: number, pack: number) => this.base.axios
        .get(`/api/voyages/${id}/toyama-invoices/${pack}`, {responseType: 'blob'})
        .then(forceDownload)

    shippingInstructions = (id: number, company_id?: number) => this.base.axios
        .get(`/api/voyages/${id}/shipping-instructions`, {
            params: {company_id},
            responseType: 'blob',
        });

    unmovables = (id: number) => this.base.axios
        .get(`/api/voyages/${id}/unmovables`, {
            responseType: 'blob',
        });

    cargoLists = (id: number, type: string, company?: number) => this.base.axios
        .get(`/api/voyages/${id}/cargo-list`, {
            responseType: 'blob',
            params: {company, type},
        });

    tallymanList = (id: number) => this.base.axios
        .get(`/api/voyages/${id}/tallyman-list`, {
            responseType: 'blob',
        });

    deliveryList = (id: number) => this.base.axios
        .get(`/api/voyages/${id}/delivery-list`, {
            responseType: 'blob',
        });

    uploadED = (id: number, file: File) => this.base.axios
        .post(`/api/voyages/${id}/ed`,
            objectToFormData({file}),
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    putNote = (id: number, note: string) => this.base.axios
        .put(`/api/voyages/${id}/note`, {note});

    ed = (voyage_id: number, company_id: number) => this.base.axios
        .get(`/api/voyages/${voyage_id}/ed/${company_id}`);

    downloadED = (voyage_id: number, company_id: number) => this.base.axios
        .get(`/api/voyages/${voyage_id}/ed/${company_id}/f`, {
            responseType: 'blob',
        });

    downloadDocPackage = (voyage_id: number, company_id?: number) => this.base.axios
        .get(`/api/voyages/${voyage_id}/docs`, {
            params: {company_id},
            responseType: 'blob',
        });

    setJpInvoicePos = (data: FormData) => this.base.axios
        .post(`/api/voyages/ed-pos`,
            data,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    getPriceTables = () => this.base.axios
        .get('/api/price-tables')
        .then(res => res.data);

    setPriceTables = (data: any) => this.base.axios
        .post('/api/price-tables',
            data,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );

    resetInvoices = (id: number) => this.base.axios
        .delete(`/api/voyages/${id}/invoices`);

    transferCars = (id: number, cars: number[]) => this.base.axios
        .post(`/api/voyages/${id}/transfer-cars`, {cars});

    cancelTransferCars = (id: number, cars: number[]) => this.base.axios
        .delete(`/api/voyages/${id}/transfer-cars`, {params: {cars}});

    deliveryFaxPreview = (id: number, company: number, type: string) => this.base.axios
        .get(`/api/voyages/${id}/delivery-fax`, {params: {company, type}});

    deliveryFaxSend = (id: number, company: number, type: string, message: string) => this.base.axios
        .post(`/api/voyages/${id}/delivery-fax`, {message, company, type});
}
